import { useEffect, useMemo, useState } from 'react';
import {
  FileText, FileSpreadsheet, FileArchive, ClipboardList,
  FileCode2, Filter, FolderOpen,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { collection, collectionGroup, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { useT } from '@/lib/i18n';
import type { messages as MSG } from '@/lib/i18n.messages';
import { fmtBytes, fmtRelative } from '@/lib/utils';
import { callSignedDownload } from '@/lib/functions';
import { listOrgQuestionnaires, watchOrgDocuments } from '@/lib/firestore';
import { PageTitle } from '@/components/PageTitle';
import { SegmentedControl } from '@/components/SegmentedControl';
import { ResourceListItem } from '@/components/ResourceListItem';
import { EmptyState } from '@/components/ui/empty-state';
import { Loader } from '@/components/ui/loader';
import { Card } from '@/components/ui/card';
import type { ExportDoc, QuestionnaireDoc, DocumentDoc, DocumentKind } from '@/types/models';

type TKey = keyof typeof MSG;

type DisplayItem =
  | { source: 'export';        data: ExportDoc }
  | { source: 'questionnaire'; data: QuestionnaireDoc }
  | { source: 'document';      data: DocumentDoc };

type FilterKind = 'all' | DocumentKind;

const EXPORT_LABEL: Record<ExportDoc['type'], TKey> = {
  pdf_executive: 'documents.export.pdf_executive',
  pdf_full:      'documents.export.pdf_full',
  excel:         'documents.export.excel',
  zip_bundle:    'documents.export.zip',
};

const KIND_LABEL: Record<DocumentKind, TKey> = {
  report:        'documents.kind.report',
  survey:        'documents.kind.survey',
  scope:         'documents.kind.scope',
  presentation:  'documents.kind.presentation',
  contract:      'documents.kind.contract',
  invoice:       'documents.kind.invoice',
  other:         'documents.kind.other',
};

function itemKind(item: DisplayItem): DocumentKind {
  if (item.source === 'export') return 'report';
  if (item.source === 'questionnaire') return 'survey';
  return item.data.kind;
}

function itemDate(item: DisplayItem): import('firebase/firestore').Timestamp {
  if (item.source === 'export') return (item.data.publishedAt ?? item.data.createdAt) as import('firebase/firestore').Timestamp;
  if (item.source === 'questionnaire') return item.data.updatedAt;
  return item.data.createdAt;
}

const KIND_ICON: Record<DocumentKind, React.ReactNode> = {
  report:        <FileText className="h-5 w-5" />,
  survey:        <ClipboardList className="h-5 w-5" />,
  scope:         <FileCode2 className="h-5 w-5" />,
  presentation:  <FileText className="h-5 w-5" />,
  contract:      <FileText className="h-5 w-5" />,
  invoice:       <FileText className="h-5 w-5" />,
  other:         <FolderOpen className="h-5 w-5" />,
};

const EXPORT_TYPE_ICON: Record<ExportDoc['type'], React.ReactNode> = {
  pdf_executive: <FileText className="h-5 w-5" />,
  pdf_full:      <FileText className="h-5 w-5" />,
  excel:         <FileSpreadsheet className="h-5 w-5" />,
  zip_bundle:    <FileArchive className="h-5 w-5" />,
};

export default function Documents() {
  const { effectiveOrgId } = useAuth();
  const { t } = useT();

  const [exports_, setExports] = useState<ExportDoc[]>([]);
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireDoc[]>([]);
  const [orgDocs, setOrgDocs] = useState<DocumentDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterKind>('all');

  useEffect(() => {
    if (!effectiveOrgId) { setLoading(false); return; }

    let done = 0;
    const tryDone = () => { done++; if (done === 3) setLoading(false); };

    const q1 = query(
      collectionGroup(db, 'exports'),
      where('orgId', '==', effectiveOrgId),
      where('publishedToClient', '==', true),
      orderBy('publishedAt', 'desc'),
    );
    const unsub1 = onSnapshot(q1,
      snap => { setExports(snap.docs.map(d => ({ id: d.id, ...d.data() } as ExportDoc))); tryDone(); },
      () => { tryDone(); },
    );

    listOrgQuestionnaires(effectiveOrgId)
      .then(qs => setQuestionnaires(qs.filter(q => q.status === 'submitted')))
      .catch(() => {})
      .finally(tryDone);

    const unsub3 = watchOrgDocuments(effectiveOrgId, docs => { setOrgDocs(docs); tryDone(); });

    return () => { unsub1(); unsub3(); };
  }, [effectiveOrgId]);

  const allItems = useMemo((): DisplayItem[] => {
    const items: DisplayItem[] = [
      ...exports_.map(d => ({ source: 'export' as const, data: d })),
      ...questionnaires.map(d => ({ source: 'questionnaire' as const, data: d })),
      ...orgDocs.map(d => ({ source: 'document' as const, data: d })),
    ];
    return items.sort((a, b) => itemDate(b).seconds - itemDate(a).seconds);
  }, [exports_, questionnaires, orgDocs]);

  const filtered = useMemo(() =>
    filter === 'all' ? allItems : allItems.filter(i => itemKind(i) === filter),
  [allItems, filter]);

  const downloadExport = async (e: ExportDoc) => {
    try {
      const { data } = await callSignedDownload({ kind: 'export', path: e.filePath });
      window.location.href = data.url;
    } catch (err) { toast.error((err as Error).message); }
  };

  const downloadDocument = async (d: DocumentDoc) => {
    if (!d.filePath) return toast.error(t('documents.file_unavailable'));
    try {
      const { data } = await callSignedDownload({ kind: 'document', path: d.filePath });
      window.location.href = data.url;
    } catch (err) { toast.error((err as Error).message); }
  };

  const filterOptions = [
    { id: 'all' as const,    label: t('q.filter.all'), icon: Filter },
    { id: 'report' as const, label: t(KIND_LABEL.report) },
    { id: 'survey' as const, label: t(KIND_LABEL.survey) },
    { id: 'scope' as const,  label: t(KIND_LABEL.scope) },
    { id: 'other' as const,  label: t(KIND_LABEL.other) },
  ];

  if (loading) return <Loader fullscreen label={t('common.loading')} />;

  return (
    <div className="space-y-6">
      <PageTitle title={t('nav.documents')} subtitle={t('documents.subtitle')} />

      {allItems.length > 0 && (
        <SegmentedControl options={filterOptions} value={filter} onChange={setFilter} />
      )}

      {filtered.length === 0 ? (
        <Card>
          <EmptyState
            className="border-0 bg-transparent py-10 dark:border-0 dark:bg-transparent"
            icon={<FileText className="h-5 w-5" />}
            title={t('documents.empty.title')}
            description={t('documents.empty.desc')}
          />
        </Card>
      ) : (
        <Card>
          <ul className="divide-y divide-slate-200 dark:divide-border/50">
            {filtered.map(item => (
              <DocumentRow
                key={`${item.source}-${item.data.id}`}
                item={item}
                onDownloadExport={downloadExport}
                onDownloadDocument={downloadDocument}
              />
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function DocumentRow({ item, onDownloadExport, onDownloadDocument }: {
  item: DisplayItem;
  onDownloadExport: (e: ExportDoc) => void;
  onDownloadDocument: (d: DocumentDoc) => void;
}) {
  const { t, locale } = useT();
  const kind = itemKind(item);
  const date = itemDate(item);

  const title = item.source === 'export'
    ? t(EXPORT_LABEL[item.data.type])
    : item.source === 'questionnaire'
      ? (item.data.filledByName
          ? t('documents.item.questionnaire', { name: item.data.filledByName })
          : t('documents.item.questionnaire_anonymous'))
      : (item.data.description || item.data.title);

  const icon = item.source === 'export'
    ? EXPORT_TYPE_ICON[item.data.type]
    : KIND_ICON[kind];

  const sizeBytes = item.source === 'export'
    ? item.data.sizeBytes
    : item.source === 'document'
      ? item.data.sizeBytes
      : undefined;

  const meta = [
    fmtRelative(date, t, locale),
    sizeBytes ? fmtBytes(sizeBytes) : null,
  ].filter(Boolean).join(' · ');

  if (item.source === 'questionnaire') {
    return (
      <ResourceListItem
        icon={icon}
        title={title}
        meta={meta}
        badge={t(KIND_LABEL[kind])}
        href={`/questionnaire/${item.data.id}`}
      />
    );
  }

  if (item.source === 'export') {
    return (
      <ResourceListItem
        icon={icon}
        title={title}
        meta={meta}
        badge={t(KIND_LABEL[kind])}
        onAction={() => onDownloadExport(item.data)}
        showDownload
      />
    );
  }

  return (
    <ResourceListItem
      icon={icon}
      title={title}
      meta={meta}
      badge={t(KIND_LABEL[kind])}
      onAction={() => onDownloadDocument(item.data)}
      showDownload
    />
  );
}

void collection;
